import React, { useEffect, useRef, useState } from 'react';
import { Drawer, Box } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

export default function UnifiedSwipeTrigger({
                                                onLeftSwipeComplete,
                                                onRightSwipeComplete,
                                                leftDrawerContent,
                                                rightDrawerContent,
                                                maxWidth = 80,
                                                edgeStart = 50,
                                                activateDistance = 10,
                                                completeThreshold = 80,
                                            }) {
    const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
    const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(0);

    const startX = useRef(null);
    const dragging = useRef(false);
    const activated = useRef(false);
    const direction = useRef(null); // 'left' or 'right'

    useEffect(() => {
        const handleTouchStart = (e) => {
            const x = e.touches[0].clientX;
            const screenWidth = window.innerWidth;

            if (onLeftSwipeComplete && x <= edgeStart) {
                direction.current = 'left';
                startX.current = x;
                dragging.current = true;
                activated.current = false;
            } else if (onRightSwipeComplete && x >= screenWidth - edgeStart) {
                direction.current = 'right';
                startX.current = x;
                dragging.current = true;
                activated.current = false;
            }
        };

        const handleTouchMove = (e) => {
            if (!dragging.current || startX.current == null) return;

            const currentX = e.touches[0].clientX;
            const diff = currentX - startX.current;

            if (direction.current === 'left') {
                if (diff <= 0) return;
                if (!activated.current && diff > activateDistance) {
                    activated.current = true;
                    setLeftDrawerOpen(true);
                }
                if (activated.current) {
                    setDrawerWidth(Math.min(diff, maxWidth));
                }
            } else if (direction.current === 'right') {
                const rightDiff = startX.current - currentX;
                if (rightDiff <= 0) return;
                if (!activated.current && rightDiff > activateDistance) {
                    activated.current = true;
                    setRightDrawerOpen(true);
                }
                if (activated.current) {
                    setDrawerWidth(Math.min(rightDiff, maxWidth));
                }
            }
        };

        const handleTouchEnd = () => {
            if (!dragging.current) return;

            const completed = drawerWidth >= completeThreshold;

            if (direction.current === 'left') {
                if (completed) {
                    setDrawerWidth(maxWidth);
                    if (typeof onLeftSwipeComplete === 'function')
                        onLeftSwipeComplete();
                    setLeftDrawerOpen(false);
                } else {
                    setLeftDrawerOpen(false);
                    setDrawerWidth(0);
                }
            }

            if (direction.current === 'right') {
                if (completed) {
                    setDrawerWidth(maxWidth);
                    if (typeof onRightSwipeComplete === 'function')
                        onRightSwipeComplete();
                    setRightDrawerOpen(false);
                } else {
                    setRightDrawerOpen(false);
                    setDrawerWidth(0);
                }
            }

            // Reset
            dragging.current = false;
            activated.current = false;
            startX.current = null;
            direction.current = null;
        };

        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        window.addEventListener('touchend', handleTouchEnd);
        window.addEventListener('touchcancel', handleTouchEnd);

        return () => {
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [drawerWidth, maxWidth, edgeStart, activateDistance, completeThreshold]);

    return (
        <>
            {/* Left Drawer */}
            <Drawer
                anchor="left"
                open={leftDrawerOpen}
                variant="temporary"
                PaperProps={{
                    sx: {
                        width: direction.current === 'left' ? drawerWidth || maxWidth : 0,
                        transition: activated.current ? 'none' : 'width 0.3s ease',
                        overflow: 'hidden',
                    },
                }}
                onClose={() => {
                    setLeftDrawerOpen(false);
                    setDrawerWidth(0);
                }}
            >
                {leftDrawerContent || (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <ArrowBackIosIcon sx={{ fontSize: '4rem', color: 'text.disabled' }} />
                    </Box>
                )}
            </Drawer>

            {/* Right Drawer */}
            <Drawer
                anchor="right"
                open={rightDrawerOpen}
                variant="temporary"
                PaperProps={{
                    sx: {
                        width: direction.current === 'right' ? drawerWidth || maxWidth : 0,
                        transition: activated.current ? 'none' : 'width 0.3s ease',
                        overflow: 'hidden',
                    },
                }}
                onClose={() => {
                    setRightDrawerOpen(false);
                    setDrawerWidth(0);
                }}
            >
                {rightDrawerContent || (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <ArrowForwardIosIcon sx={{ fontSize: '4rem', color: 'text.disabled' }} />
                    </Box>
                )}
            </Drawer>
        </>
    );
}
